import React, { useState, useEffect, useRef, useCallback, useContext } from 'react';
import { useAtom } from 'jotai';
import axios from 'axios';
import { useHistory, useLocation } from "react-router-dom";
import { IoPeopleCircleSharp, IoHourglassOutline } from "react-icons/io5";
import { MdFavorite } from "react-icons/md";
import { FaArrowUp } from "react-icons/fa";
import LayoutHomeUser from '../Layouts/Admin/Layout';
import TextToHTML from '../Components/TextToHtml';
import LoadingSpinner from '../Components/LoadingSpinner';
import LikeButton from '../Components/LikeButton';
import FluidMediaModal from '../Components/feedmediamodal';
import config from '../Config';
import DarkModeContext from '../Components/DarkModeContext';
import { isAuthAtom, authUserAtom } from '../Services/store/atoms';
import ScrollToTopButton from '../Components/ScrollToTopButton';

function FeedPublico(props) {
  const [isAuth] = useAtom(isAuthAtom);
  const [authUser] = useAtom(authUserAtom);
  const { darkMode } = useContext(DarkModeContext);
  const observerRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [nextPageUrl, setNextPageUrl] = useState(null);
  const [activeTab, setActiveTab] = useState('all');
  const [selectedGenre, setSelectedGenre] = useState('All');
  const [hasFavorites, setHasFavorites] = useState(false);
  const history = useHistory();
  const location = useLocation();
  const [showMediaModal, setShowMediaModal] = useState(false);
  const [selectedMediaUrls, setSelectedMediaUrls] = useState([]);
  const [mediaClickIndex, setMediaClickIndex] = useState(0);

  const apiEndpoint = isAuth
    ? `${config.REACT_APP_BASE_URL}/api/v1/home/FeedPublicAuth`
    : `${config.REACT_APP_BASE_URL}/api/v1/home/FeedPublic`;

  const handleScroll = (isScrollingUp) => {
    setShowHeader(isScrollingUp);
  };

  const fetchProducts = useCallback((pageUrl, myFavorites = false, genre = null) => {
    setIsLoading(true);
    const headers = {};
    if (authUser?.token) {
      headers.Authorization = `Bearer ${authUser.token}`;
    }
    
    // Use the provided genre or the current selectedGenre state
    const genreToUse = genre !== null ? genre : selectedGenre;
    
    const params = { 
      myFavorites,
      genre: genreToUse !== 'All' ? genreToUse : undefined
    };

    axios.post(pageUrl || apiEndpoint, params, { headers })
      .then((response) => {
        const newProducts = response.data.data.map(product => ({
          ...product,
          liked: false,
          likeCount: product.likes_count || 0
        }));

        setProducts(prevProducts => {
          const uniqueProducts = newProducts.filter(newProduct =>
            !prevProducts.some(prevProduct => prevProduct.id === newProduct.id)
          );
          return pageUrl ? [...prevProducts, ...uniqueProducts] : uniqueProducts;
        });

        setNextPageUrl(response.data.next_page_url);

        if (newProducts.some(product => product.favorites_exists)) {
          setHasFavorites(true);
        }

        setTimeout(() => setIsLoading(false), 500); // Set isLoading to false after fetching products
      })
      .catch(function (error) {
        console.error(error);
        setIsLoading(false); // Set isLoading to false in case of error
      });
  }, [authUser?.token, apiEndpoint, selectedGenre]);

  useEffect(() => {
    setProducts([]);
    fetchProducts(apiEndpoint, activeTab === 'favorites');
  }, [apiEndpoint, activeTab, fetchProducts]);

  const lastProductElementRef = useCallback(node => {
    if (isLoading || !nextPageUrl) return;
    if (observerRef.current) observerRef.current.disconnect();
    observerRef.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && nextPageUrl) {
        fetchProducts(nextPageUrl, activeTab === 'favorites');
      }
    }, {
      rootMargin: '50% 0px', // Carga la siguiente página cuando el usuario llegue al 50% del contenido
    });
    if (node) observerRef.current.observe(node);
  }, [isLoading, nextPageUrl, fetchProducts, activeTab]);

  const scrollToTop = () => {
    const content = document.getElementById('feed-content');
    if (content) {
      content.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  };

  const onCardClick = (userId) => {
    history.push(`/profileModal/${userId}`, { background: location });
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSelectedGenre('All');
    setProducts([]);
    fetchProducts(apiEndpoint, tab === 'favorites', 'All');
    scrollToTop();
  };

  const handleGenreChange = (genre) => {
    const newGenre = selectedGenre === genre ? 'All' : genre; // Toggle genre filter
    setSelectedGenre(newGenre);
    setProducts([]); // Clear existing products
    // Fetch products with the current tab and new genre filter
    fetchProducts(apiEndpoint, activeTab === 'favorites', newGenre);
  };

  const handleMediaClick = (mediaUrls, index = 0) => {
    setSelectedMediaUrls(mediaUrls);
    setMediaClickIndex(index);
    setShowMediaModal(true);
  };

  return (
    <LayoutHomeUser>
      <div className="flex flex-col items-center">
        <div className="flex flex-col items-center my-4">
          {isAuth ? (
            <div className="flex mb-4">
              <button
                className={`px-4 py-2 mx-2 border-2 rounded-md ${activeTab === 'all' ? 'bg-rose-500 text-white' : 'border-rose-500 text-rose-500 bg-white dark:bg-gray-800 dark:text-white'}`}
                onClick={() => handleTabChange('all')}
              >
                <div className='flex flex-row'>
                  <IoPeopleCircleSharp className='-mt-1 text-3xl' />
                  <div>All</div>
                </div>
              </button>
              <button
                className={`px-4 py-2 mx-2 border-2 rounded-md ${activeTab === 'favorites' ? 'bg-rose-500 text-white' : 'border-rose-500 text-rose-500 bg-white dark:bg-gray-800 dark:text-white'}`}
                onClick={() => handleTabChange('favorites')}
              >
                <div className='flex flex-row'>
                  <MdFavorite className='-mt-1 text-3xl' />
                  <div>My Favorites</div>
                </div>
              </button>
            </div>
          ) : null}

          <div className="flex flex-wrap gap-2 justify-center mt-2">
            {[
              ...(!isAuth ? ['All'] : []),
              'female', 'male', 'trans'
            ].map((genre) => (
              <button
                key={genre}
                onClick={() => handleGenreChange(genre)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors duration-200 ${selectedGenre === genre ? 'bg-rose-500 text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600'}`}
              >
                {genre.charAt(0).toUpperCase() + genre.slice(1)}
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-col justify-center items-center w-full">
          {products.map((val, key) => {
            const isLastProduct = products.length === key + 1;
            return (
              <div
                key={key}
                ref={isLastProduct ? lastProductElementRef : null}
                className={`relative mb-8 mt-4 p-4 rounded-lg shadow-md max-w-2xl w-full mx-2 ${darkMode ? 'bg-gray-700' : 'bg-white'}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center">
                    <div className="w-20 h-20 rounded-full overflow-hidden cursor-pointer border-8 border-white">
                      <a onClick={() => onCardClick(val.user_id)}>
                        <img src={val.avatar_thumb_url || val.avatar} alt="User Avatar" className="object-cover w-full h-full" />
                      </a>
                    </div>
                    <div className="ml-4">
                      <div className={`text-2xl font-bold capitalize ${darkMode ? 'text-white' : 'text-gray-600'}`}>{val.user_date.nickname}</div>
                    </div>
                  </div>
                  <LikeButton feedId={val.id} />
                </div>
                <div className={`bg-gray-100 p-3 rounded-lg -mt-4 ${darkMode ? 'bg-gray-700 text-white' : 'text-gray-800'}`}>
                  <TextToHTML text={val.description} />
                </div>
                <div className={`mt-2 h-full w-full overflow-x-auto flex items-center justify-center ${val.files.length > 1 ? 'grid grid-cols-2 gap-4' : 'flex flex-row space-x-4'}`}>
                  {val.files.map((file, index) => (
                    <img
                      key={index}
                      src={file.url}
                      alt={`File ${index}`}
                      className="object-cover h-full cursor-pointer rounded-lg"
                      onClick={() => handleMediaClick(val.files.map(file => file.url), index)}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex justify-center items-center py-4">
          {isLoading && products.length > 0 && <LoadingSpinner />}
        </div>

        <ScrollToTopButton />

        {showMediaModal && (
          <FluidMediaModal
            show={showMediaModal}
            onClose={() => setShowMediaModal(false)}
            mediaUrls={selectedMediaUrls}
            startIndex={mediaClickIndex}
          />
        )}
      </div>
    </LayoutHomeUser>
  );
}

export default FeedPublico;
